import {FullyAccessibleGrid} from 'constants/SampleConstants';
import {CreatePlatePaneState, DEFAULT_CREATE_PLATE_PANE_STATE} from 'samples/CreatePlatePane';
import {
  AnnotateFillPlateModalStep,
  DefineExistingPlateState,
  PlateDefinitionType,
  PlateDesignState,
  PlateMapType,
} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {PlateDataParameter, PlateMap} from 'samples/plate-maps/PlateMapConstants';
import {ValidationErrorsByLayerAndLevel} from 'samples/plate-maps/validation/ValidationTypes';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {StepItemStatus} from 'ui-library/StepNav';

export interface AnnotateFillPlateModalState {
  initialPlateMap: PlateMap | null;
  plateMapType: PlateMapType | null;
  /**
   * The container schema that corresponds to the chosen plate schema.
   *
   * NOTE: This field is effectively derived from the following sources:
   *
   * 1. PlateMapType.DESIGN
   *      plateDesignState.plateSchema.containerSchemaId
   *      via CREATE_NEW_PLATE_MAP
   * 2. PlateMapType.RECORD -> PlateDefinitionType.CREATE:
   *      createPlatePaneState.selectedSchema.containerSchemaId
   *      via CREATE_NEW_PLATE_MAP
   * 3. PlateMapType.RECORD -> PlateDefinitionType.EXISTING
   *      selectedGrid.storableSchema.containerSchemaId
   *      via UPDATE_INITIAL_PLATE_MAP
   */
  containerSchemaId: string | null;
  stepKey: AnnotateFillPlateModalStep;
  statusByStepKey: Record<AnnotateFillPlateModalStep, StepItemStatus>;
  /** Only relevant if plateMapType == PlateMapType.RECORD */
  plateDefinitionType: PlateDefinitionType;
  createPlatePaneState: CreatePlatePaneState;
  existingPlateState: DefineExistingPlateState;
  plateDesignState: PlateDesignState;
  isCreatingPlateRecordFromDesign: boolean;
  selectedGrid: FullyAccessibleGrid | null;
  dismissablePlateMapValidation: ValidationErrorsByLayerAndLevel;
}

export const DEFAULT_DEFINE_EXISTING_PLATE_STATE: DefineExistingPlateState = {
  selectedGridId: null,
  isValidating: false,
  error: null,
  tags: [],
  appendStudyIds: [],
};

export const DEFAULT_PLATE_DESIGN_STATE: PlateDesignState = {
  plateSchema: null,
};

export const DEFAULT_ANNOTATE_FILL_PLATE_MODAL_STATE: AnnotateFillPlateModalState = {
  initialPlateMap: null,
  plateMapType: null,
  containerSchemaId: null,
  stepKey: AnnotateFillPlateModalStep.DEFINE_PLATE,
  statusByStepKey: {
    [AnnotateFillPlateModalStep.DEFINE_PLATE]: StepItemStatus.UNVISITED,
    [AnnotateFillPlateModalStep.DEFINE_PLATE_MAP]: StepItemStatus.UNVISITED,
    [AnnotateFillPlateModalStep.TRANSFER_QUANTITIES]: StepItemStatus.UNVISITED,
  },
  plateDefinitionType: PlateDefinitionType.CREATE,
  createPlatePaneState: DEFAULT_CREATE_PLATE_PANE_STATE,
  existingPlateState: DEFAULT_DEFINE_EXISTING_PLATE_STATE,
  plateDesignState: DEFAULT_PLATE_DESIGN_STATE,
  isCreatingPlateRecordFromDesign: false,
  selectedGrid: null,
  dismissablePlateMapValidation: {},
};

export enum AnnotateFillPlateModalReducerActionType {
  UPDATE_INITIAL_PLATE_MAP = 'UPDATE_INITIAL_PLATE_MAP',
  CREATE_NEW_PLATE_MAP = 'CREATE_NEW_PLATE_MAP',
  UPDATE_STEP_KEY = 'UPDATE_STEP_KEY',
  UPDATE_PLATE_DEFINITION_TYPE = 'UPDATE_PLATE_DEFINITION_TYPE',
  UPDATE_PLATE_MAP_TYPE = 'UPDATE_PLATE_MAP_TYPE',
  UPDATE_CREATE_PLATE_PANE_STATE = 'UPDATE_CREATE_PLATE_PANE_STATE',
  UPDATE_EXISTING_PLATE_STATE = 'UPDATE_EXISTING_PLATE_STATE',
  UPDATE_PLATE_DESIGN_STATE = 'UPDATE_PLATE_DESIGN_STATE',
  DISMISS_VALIDATION_ERROR = 'DISMISS_VALIDATION_ERROR',
}

interface AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType;
}

interface UpdateInitialPlateMapAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.UPDATE_INITIAL_PLATE_MAP;
  updatedPlateMapInfo: {
    plateMap: PlateMap;
    containerSchemaId: string;
  } | null;
  isInProcedure: boolean;
  plateDataParameters: Array<PlateDataParameter>;
}

interface UpdateStepKeyAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.UPDATE_STEP_KEY;
  stepKey: AnnotateFillPlateModalStep;
}

interface CreateNewPlateMapAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.CREATE_NEW_PLATE_MAP;
  containerSchemaId: string;
  containerSchemaFields: Array<SchemaField>;
  plateDataParameters?: Array<PlateDataParameter>;
}

interface UpdatePlateMapTypeAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_MAP_TYPE;
  plateMapType: PlateMapType;
}

interface UpdatePlateDefinitionTypeAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_DEFINITION_TYPE;
  plateDefinitionType: PlateDefinitionType;
}

interface UpdateCreatePlatePaneStateAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.UPDATE_CREATE_PLATE_PANE_STATE;
  createPlatePaneState: CreatePlatePaneState;
}

interface UpdateExistingPlateStateAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.UPDATE_EXISTING_PLATE_STATE;
  existingPlateState: DefineExistingPlateState;
}

interface UpdatePlateDesignStateAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_DESIGN_STATE;
  plateDesignState: PlateDesignState;
}

interface DismissValidationErrorAction extends AnnotateFillPlateModalAction {
  type: AnnotateFillPlateModalReducerActionType.DISMISS_VALIDATION_ERROR;
  errorUuids: Set<string>;
}

export type AnnotateFillPlateModalReducerAction =
  | UpdateInitialPlateMapAction
  | UpdateStepKeyAction
  | CreateNewPlateMapAction
  | UpdatePlateDefinitionTypeAction
  | UpdateCreatePlatePaneStateAction
  | UpdateExistingPlateStateAction
  | UpdatePlateDesignStateAction
  | UpdatePlateMapTypeAction
  | DismissValidationErrorAction;

/**
 * Reset form state to support switching between the 3 modes of the modal:
 *
 * 1. PlateMapType.DESIGN
 * 2. PlateMapType.RECORD -> PlateDefinitionType.EXISTING
 * 3. PlateMapType.RECORD -> PlateDefinitionType.CREATE
 *
 * NB: We could do more targeted reset where we preserve analogous state between
 * modes, but for now, we reset mostly everything... apart from the registry, studies, and plate schema that
 * were selected for PlateDefinitionType.CREATE. Studies is preserved because it can be seeded via
 * initialStudyIds.
 */
export function resetDesignCreateExistingPlateStates(
  prevState: AnnotateFillPlateModalState
): AnnotateFillPlateModalState {
  const resetState: AnnotateFillPlateModalState = {
    ...prevState,
    createPlatePaneState: {
      ...DEFAULT_CREATE_PLATE_PANE_STATE,
      selectedSchema: prevState.createPlatePaneState.selectedSchema,
      selectedContainerSchema: prevState.createPlatePaneState.selectedContainerSchema,
      registry: prevState.createPlatePaneState.registry,
      studyIds: prevState.createPlatePaneState.studyIds,
    },
    existingPlateState: {
      ...DEFAULT_DEFINE_EXISTING_PLATE_STATE,
      appendStudyIds: prevState.existingPlateState.appendStudyIds,
    },
    selectedGrid: null,
  };

  if (prevState.isCreatingPlateRecordFromDesign) {
    return resetState;
  } else {
    return {
      ...resetState,
      initialPlateMap: null,
      containerSchemaId: null,
      plateDesignState: DEFAULT_PLATE_DESIGN_STATE,
    };
  }
}
